import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import ReactLoading from "react-loading";
import HomeProductCard from "../../../product-card-home/HomeProductCard";
import styles from "./ProductSection.module.scss";
import HomeHeroCardImage from "../../../../assets/images/pages/home/hero-section/homeheroCard.jpeg";
import { RootState } from "../../../../redux/store";
import { usePopularProducts } from "../../../../hooks/useHomeQueries";

const ProductSection: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage] = useState<number>(24);
  const [accumulatedProducts, setAccumulatedProducts] = useState<any[]>([]);
  const [hasMorePages, setHasMorePages] = useState<boolean>(true);

  const selectedLanguage = useSelector(
    (state: RootState) => state.language.selectedLanguage?.code
  );
  const translations = useSelector(
    (state: RootState) => state.translations.data
  );
  // Reset pagination when language changes
  useEffect(() => {
    setCurrentPage(1);
    setAccumulatedProducts([]);
    setHasMorePages(true);
  }, [selectedLanguage]);

  // Fetch popular products using partner/popular endpoint
  const {
    data: popularProductsResponse,
    isLoading: isPopularLoading,
    isFetching: isPopularFetching,
    isError: isPopularError,
  } = usePopularProducts(selectedLanguage, currentPage, perPage);

  // Extract products and append for pagination
  useEffect(() => {
    if (!popularProductsResponse) return;

    const incomingProducts: any[] = Array.isArray(popularProductsResponse)
      ? popularProductsResponse
      : Array.isArray(popularProductsResponse?.data)
      ? popularProductsResponse.data
      : Array.isArray(popularProductsResponse?.data?.data)
      ? popularProductsResponse.data.data
      : [];

    if (currentPage === 1) {
      setAccumulatedProducts(incomingProducts);
    } else {
      setAccumulatedProducts((previousProducts) => {
        const existingProductIds = new Set(
          previousProducts.map((item: any) => item.id)
        );
        const uniqueIncomingProducts = incomingProducts.filter(
          (item: any) => !existingProductIds.has(item.id)
        );
        return [...previousProducts, ...uniqueIncomingProducts];
      });
    }

    // Determine pagination status
    const pagination =
      popularProductsResponse?.pagination ||
      popularProductsResponse?.data?.pagination;

    if (pagination) {
      if (typeof pagination.has_more === "boolean") {
        setHasMorePages(pagination.has_more);
        return;
      }
      if (pagination.last_page && pagination.current_page) {
        setHasMorePages(pagination.current_page < pagination.last_page);
        return;
      }
    }

    if (incomingProducts.length < perPage) {
      setHasMorePages(false);
    }
  }, [popularProductsResponse, currentPage, perPage]);

  // Load more popular products handler
  const handleLoadMoreClick = () => {
    if (isPopularFetching || !hasMorePages) return;
    setCurrentPage((prevPage) => prevPage + 1);
  };

  // Ensure no empty gaps in 2, 3, or 4 column grids while more items are available
  const displayedProducts = useMemo(() => {
    if (!hasMorePages) return accumulatedProducts;
    const remainder = accumulatedProducts.length % 12;
    if (remainder !== 0 && accumulatedProducts.length > 12) {
      return accumulatedProducts.slice(0, accumulatedProducts.length - remainder);
    }
    return accumulatedProducts;
  }, [accumulatedProducts, hasMorePages]);

  const isInitialLoading = isPopularLoading && currentPage === 1;
  const isLoadingMore = isPopularFetching && currentPage > 1;

  return (
    <section className={styles.productSection} id="products-section">
      <div className="container">
        {/* Title Section */}
        <div className={styles.titleContent}>
          <div className={styles.titleRow}>
            <h2 className={styles.title}>
              {translations?.popular_products || "Populyar məhsullar"}
            </h2>
            <Link to="/products" className={styles.allLink}>
              {translations?.view_all || "Hamısına bax"} &rarr;
            </Link>
          </div>
        </div>

        {/* Product Grid / Skeletons / Empty / Error */}
        <div className={styles.cardBox}>
          {isInitialLoading ? (
            Array(8)
              .fill(0)
              .map((_, index) => (
                <div key={index} className={styles.skeletonCard}>
                  <div className={styles.skeletonImage} />
                  <div className={styles.skeletonBody}>
                    <div className={styles.skeletonTag} />
                    <div className={styles.skeletonTitleLine} />
                    <div className={styles.skeletonTitleLineShort} />
                    <div className={styles.skeletonPrice} />
                    <div className={styles.skeletonButton} />
                  </div>
                </div>
              ))
          ) : isPopularError && displayedProducts.length === 0 ? (
            <p className={styles.errorText}>
              {translations?.network_error || "Məlumat yüklənərkən xəta baş verdi"}
            </p>
          ) : displayedProducts.length > 0 ? (
            displayedProducts.map((product: any, index: number) => (
              <HomeProductCard
                key={product.id ? `${product.id}-${index}` : index}
                productId={String(product.id)}
                image={product.cover_image || HomeHeroCardImage}
                title={product.name}
                discount_percentage={product.discount_percentage}
                discount_price={product.discount_price}
                original_price={product.original_price}
                promotion_price={product.promotion_price}
                price={product.price}
                discount={product.discount_price}
                currency={product.currency}
                channel={
                  product.channel ||
                  product.marketplace ||
                  product.vendor ||
                  product.provider ||
                  product.source
                }
                buttonText={translations?.add_to_basket || "Səbətə at"}
                wishlistStatus={Boolean(
                  product.is_fav ?? product.wishlist_status ?? product.is_favorite
                )}
                isNew={product.is_new}
                slug={product.slug}
              />
            ))
          ) : (
            <p className={styles.errorText}>
              {translations?.no_products || "Populyar məhsul tapılmadı"}
            </p>
          )}
        </div>

        {/* Load More Button */}
        {!isInitialLoading && displayedProducts.length > 0 && hasMorePages && (
          <div className={styles.more_btn}>
            <button
              type="button"
              onClick={handleLoadMoreClick}
              className={styles.moreBtn}
              disabled={isLoadingMore}
              aria-label={translations?.show_more || "Daha çox"}
              tabIndex={0}
            >
              {isLoadingMore ? (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <ReactLoading
                    type="spin"
                    color="#ffffff"
                    height={18}
                    width={18}
                  />
                  <span>{translations?.loading || "Yüklənir..."}</span>
                </div>
              ) : (
                <span>{translations?.show_more || "Daha çox"}</span>
              )}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductSection;
